import React, { useState, useEffect } from 'react';
import { Container, CircularProgress, Backdrop, Button } from '@material-ui/core';
import ShedApi from '../../../services/ShedApi';
import Grid from '@material-ui/core/Grid';
import {
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import SelectShedMovementType from '../../../components/Form/SelectShedMovementType';
import { useStyles } from './Style'
import PageTitle from '../../../components/ui/PageTitleUI'
import RegistrationList from './RegistrationList';
import ResponsibleUserList from './ResponsibleUserList';
import TextField from '../../../components/Form/TextField';
import { useAuth } from '../../../hooks/auth';

type Props = {
    shedId: number,
    movementTypeId: number,
    movementTypeDescription: string,
    clickHandlerClose?: () => void,
    handleSetSuccess?: (value: boolean) => void,

}

interface IFormPanel {
    shed:any;
    movementType :any;
    registrations: any;
    users: any;
    responsibleUser: any;
    movementTypeFix: any;
    shedFix: any;
}

const EditShedRegistrationForm: React.FC<Props> = (props) => {
    const classes = useStyles();
    
    const [openBackdrop, setOpenBackdrop] = useState(false)
    const { t: tGeneral } = useTranslation("General")
    const { t: tShed } = useTranslation("Shed")
    const { t: tVal } = useTranslation("Validation")
    const { enqueueSnackbar } = useSnackbar()
    const [shed, setShed] = useState<any>(null)
    const [movementTypeId, setMovementTypeId] = useState<any>(null)
    useAuth();

    const { handleSubmit, control, getValues, setError, setValue, clearErrors } = useForm<IFormPanel>({
        defaultValues: {
            shed: {},
            movementType: {},
            users: [],
            registrations: [],
            responsibleUser: {}
        }
    });

    useEffect(() => {

        if (props.shedId && props.shedId !== 0) {
            getModel(true)
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.shedId, props.movementTypeId]);

    const getModel = (fromProps: boolean) => {

        setOpenBackdrop(true)
        clearErrors()

        var shedId = 0
        var movementTypeId = 0

        var errors = false

        if (fromProps) {
            shedId = props.shedId
            movementTypeId = props.movementTypeId
        }
        else {
            var data = getValues()  
            shedId = data.shed?.code
            movementTypeId = data.movementType?.code
        }

        if (shedId && shedId === 0) {
            setError("shed" , { message: tVal("requiredField") })
            errors = true
        }

        if (movementTypeId && movementTypeId  === 0){
            setError("movementType" , { message: tVal("requiredField") })
            errors = true
        }

        if (errors)
            return

        setMovementTypeId(movementTypeId)

        ShedApi.get(shedId).then((response: any) => {
            setShed(response)

            var shedMovementType = response.movementTypes.filter((element: { movementTypeId: number; }) => element.movementTypeId === movementTypeId)[0];

            if(shedMovementType) {
                setValue('registrations', shedMovementType.registrations)
                setValue('users', shedMovementType.users)
                
                if (props.shedId && props.shedId !== 0){
                    setValue("shedFix", response.code + ' - ' + (response.type === "D" ? tShed('unload') : tShed('load')) )
                    setValue("movementTypeFix", props.movementTypeDescription )
                }
            }
        })
        .finally(() => {setOpenBackdrop(false)})
    }

    const onSubmit = async (data: any) => {
        
        setOpenBackdrop(true)

        var model = {
            users: data.users,
            registrations: data.registrations,
            shedId: shed.id,
            movementTypeId: movementTypeId
          }

        ShedApi.updateResponsibleUser(model)
        .then(() => { 
            enqueueSnackbar(tGeneral("returnSubmitEditFormSuccess"), { variant: 'success' })
            if (props && props.handleSetSuccess)
                props.handleSetSuccess(true);
        })
        .finally(() => {
            setOpenBackdrop(false)
        })
        
    } 

    const onChangeMovementType = (data: any) => {
        getModel(false)
    }

    return (
        <Container maxWidth={false} component="main">
            <React.Fragment>
                <Backdrop className={classes.backdrop} open={openBackdrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => {if (e.key === 'Enter') e.preventDefault()} } >
                    <Grid container spacing={1}>

                        <Grid item xs={5}>
                            <PageTitle title={props.shedId === 0 ? tShed("addRegistrations") : tShed("editRegistrations")} style={{ marginLeft: "40px" }} />
                        </Grid>
                        
                        <Grid item xs={7} style={{display:'flex', justifyContent:'right'}}>
                            <Button 
                                variant="contained" 
                                className={classes.rootCancelButton} 
                                startIcon={<CancelIcon />}
                                onClick={() => props && props.clickHandlerClose ? props.clickHandlerClose() : null}>
                                {tGeneral("cancel")}
                            </Button>
                            <Button  
                                type="submit"
                                variant="contained" 
                                color="primary" 
                                startIcon={<SaveIcon />}
                                className={classes.rootButton}>
                                {tGeneral("save")}
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                        {props.shedId && props.shedId > 0 ?
                        <>
                            <Grid item xs={2}>
                                <TextField
                                    label={tShed("shed")}
                                    name="shedFix"
                                    control={control} 
                                    fullWidth
                                    InputLabelProps={{shrink:true}}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked
                                        }
                                    }} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label={tShed("movementType")}
                                    name="movementTypeFix"
                                    control={control} 
                                    fullWidth
                                    InputLabelProps={{shrink:true}}
                                    InputProps={{
                                        readOnly: true,
                                        classes: {
                                            input: classes.inputBlocked
                                        }
                                    }} />
                            </Grid>
                        </>
                        :
                        <>
                            <SelectShedMovementType
                                nameSelectShed={'shed'} 
                                nameSelectMovementType={'movementType'} 
                                control={control}
                                setValue={setValue}
                                onChangeMovementType={onChangeMovementType}/>  
                        </>
                        }
                    </Grid> 

                    {shed == null ? <></> :         
                        <Grid container spacing={1}>
                            <Grid item xs={7} style={{ marginRight: 50 }}>
                                <RegistrationList
                                    name={"registrations"}
                                    allowAdd={true}
                                    allowDelete={true}
                                    control={control}/>    
                            </Grid>
                            <Grid item xs={4}>
                                <ResponsibleUserList
                                    name={"users"}
                                    allowAdd={true}
                                    allowDelete={true}
                                    control={control}/>  
                            </Grid>
                        </Grid>
                    }
                </form>
            </React.Fragment>
        </Container >
    )
}
export default EditShedRegistrationForm
